import {Salutation} from "./salutation";
import {LegalForm} from "./legal-form";
import {LegalType} from "./legal-type";
import {
  companyToday,
  DayKey,
  DEFAULT_COMPANY_TIME_ZONE,
  isDayKey,
  isValidTimeZone
} from "../../../../_shared/time/company-day";

export interface ILegalInformation {
  get legalType(): LegalType;

  as<T extends ILegalInformation>(): T;

  asAny(): any;
}

export abstract class LegalInformation implements ILegalInformation {
  abstract get legalType(): LegalType;

  static parse(obj: any): LegalInformation {
    if (obj.legalForm) {
      return LegalEntityInformation.parse(obj)
    }
    if (obj.firstName) {
      return NaturalPersonInformation.parse(obj);
    }
    throw new Error("Unknown legal information type");
  }

  as<T extends ILegalInformation>(): T {
    return this as unknown as T;
  }

  asAny(): any {
    return this;
  }
}

export interface INaturalPersonInformation extends ILegalInformation {
  salutation: Salutation;
  firstName: string;
  lastName: string;
  /** A calendar day, `yyyy-MM-dd`, sent and received as it is; the server stores it as a date (#795). */
  dateOfBirth: DayKey;
  identificationNumber: string;
}

/** The browser's own IANA zone, or the company default when the engine does not name one. */
function browserTimeZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(zone) ? zone : DEFAULT_COMPANY_TIME_ZONE;
}

/**
 * The calendar day of a birth date as the API sends it (#795), for the `<input type="date">` of the owner forms.
 *
 * The API sends the day as a plain `yyyy-MM-dd`, and the form shows it as it is. It is never read through a `Date`:
 * the old `InputDate` did that, in the browser's zone, and showed an admin in New York the day before.
 *
 * @returns the day key, or `''` for anything that is not a `yyyy-MM-dd` day (the input then shows nothing, and the
 * required field has to be filled in again rather than a guessed day being saved).
 */
export function birthDateDayKey(value: unknown): DayKey {
  return isDayKey(value) ? value : '';
}

export class NaturalPersonInformation extends LegalInformation implements INaturalPersonInformation {
  salutation: Salutation = Salutation.Male;
  firstName: string = '';
  lastName: string = '';
  identificationNumber: string = '';

  /**
   * Starts on today's date as the admin's calendar shows it, as the form always has. Read through `Intl` with the
   * browser's zone named explicitly rather than through `Date`'s local getters, so the specs can prove that
   * nothing else on these forms depends on the machine's zone.
   */
  dateOfBirth: DayKey = companyToday(browserTimeZone());

  get legalType(): LegalType {
    return LegalType.NaturalPerson;
  }

  static parse(obj: any): NaturalPersonInformation {
    const info = new NaturalPersonInformation();
    Object.assign(info, obj);
    info.dateOfBirth = birthDateDayKey(obj.dateOfBirth);
    return info;
  }
}

export interface ILegalEntityInformation extends ILegalInformation {
  legalForm: LegalForm;
  name: string;
}

export class LegalEntityInformation extends LegalInformation implements ILegalEntityInformation {
  legalForm: LegalForm = LegalForm.GmbH;
  name: string = '';

  get legalType(): LegalType {
    return LegalType.LegalEntity;
  }

  static parse(obj: any): LegalEntityInformation {
    const info = new LegalEntityInformation();
    Object.assign(info, obj);
    return info;
  }
}
